import { Stack, Typography, Box, Chip, Tooltip } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { AxisCard, NotCollected, AxisLoading, AxisError } from './ProductAxisCard';

// #108 Phase 2 — 제품 축의 사용량 카드. 데이터: GET /api/usage/portfolio 를 제품 id 로 필터.
//
// 🔴 요청 수는 사용량이 아니다. 이 화면의 가장 큰 함정이라 반드시 드러낸다.
//   8월 실측: 전 존 요청 6,697만 중 실제 브라우저 페이지 조회는 64,970 — 1,031:1.
//   api.raymedical.com(EMS 장비 연동) 하나가 raymedical 존의 96% 를 차지한다.
//   "요청" 을 방문자 수로 읽히게 두면 그 화면은 거짓말을 한다.
//
// 🔴 사람 PV 를 제품 단위로 만들지 않는다.
//   호스트 차원 데이터셋에는 봇/사람 구분이 없다. 근거가 없으므로 만들어내지 않고,
//   존 단위로만 /usage 전사 화면에 싣는다.
//
// 🔴 coverage 를 항상 보여준다. "8일/30일" 이 안 보이면 8일치를 한 달치로 읽는다.

const n = (v) => (v ?? 0).toLocaleString('ko-KR');

const TYPE_LABEL = { web: '웹', api: 'API', infra: '기반', internal: '사내', env: '개발·테스트' };

export default function ProductUsageCard({ productId, state }) {
  const { data, error, loading } = state;
  const row = data?.products?.find((p) => p.id === productId) || null;

  const period = data?.period?.value;
  const cov = data?.coverage;
  const partial = cov && cov.days_observed < cov.days_in_period;

  const card = (children, action) => (
    <AxisCard
      title={`사용량${period ? ` · ${period}` : ''}`}
      icon={<QueryStatsIcon sx={{ fontSize: 19, color: 'text.secondary' }} />}
      source={`${data?.sourceRepo || 'prd-raymedical-homepage'}/${data?.sourcePath || 'docs/usage/usage-snapshot.json'}`}
      sourceUrl="https://github.com/rayrndsw/prd-raymedical-homepage/blob/main/docs/usage/usage-snapshot.json"
      updatedAt={data?.updated_at ? String(data.updated_at).slice(0, 10) : null}
      action={action}
    >
      {children}
    </AxisCard>
  );

  if (loading) return card(<AxisLoading />);
  if (error) return card(<AxisError message="사용량을 불러오지 못했습니다" hint={error} />);

  if (data && data.schemaOk === false) {
    return card(
      <AxisError
        message="사용량 계약 버전이 다릅니다"
        hint={`받은 스키마: ${data.schema || '(없음)'} — 화면이 기대하는 것은 ray-usage-snapshot/v1 입니다. 숫자를 그리지 않습니다.`}
      />,
    );
  }

  if (!row) {
    return card(
      <NotCollected reason="이 제품에 매핑된 서비스 도메인의 트래픽이 관측되지 않았습니다. 제품 SSOT 의 hosts 가 비어 있거나, 해당 호스트가 우리 Cloudflare 존 밖일 수 있습니다." />,
    );
  }

  return card(
    <Stack spacing={1.25}>
      <Stack direction="row" flexWrap="wrap" gap={2.5} alignItems="flex-end">
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.1 }}>
            {n(row.live_requests)}
          </Typography>
          <Tooltip title="봇·기계 호출을 포함한 요청 수입니다. 방문자 수가 아닙니다.">
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', borderBottom: '1px dotted', cursor: 'help', display: 'inline-block' }}>
              운영 요청 (봇 포함)
            </Typography>
          </Tooltip>
        </Box>
        {row.env_requests > 0 && (
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', lineHeight: 1.1, color: 'text.secondary' }}>
              {n(row.env_requests)}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>개발·테스트 (제외)</Typography>
          </Box>
        )}
      </Stack>

      {/* 호스트별 내역 — api 는 프런트엔드가 없으므로 방문 지표로 읽을 대상이 아니다 */}
      <Stack spacing={0.4}>
        {(row.hosts || []).map((h) => (
          <Stack key={h.host} direction="row" alignItems="center" gap={1}
            sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            <Chip size="small" variant="outlined" label={TYPE_LABEL[h.type] || h.type}
              sx={{ height: 17, fontSize: 10, minWidth: 46 }} />
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{h.host}</Box>
            {h.type === 'api' && (
              <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>
                프런트엔드 없음
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{n(h.requests)}</Box>
          </Stack>
        ))}
      </Stack>

      {partial && (
        <Typography sx={{ fontSize: 12, color: 'warning.main', lineHeight: 1.6 }}>
          이 달 <b>{cov.days_in_period}일 중 {cov.days_observed}일</b>만 관측된 값입니다 — 한 달치가 아닙니다.
        </Typography>
      )}
      <Typography sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1.6 }}>
        사람이 실제로 본 페이지 수는 제품 단위로 나눌 근거가 없어 표시하지 않습니다 — 존 단위로만
        전사 화면에서 봅니다.
      </Typography>
    </Stack>,
    partial ? <Chip size="small" color="warning" variant="outlined" label={`${cov.days_observed}/${cov.days_in_period}일`} sx={{ height: 20, fontSize: 11 }} /> : null,
  );
}
